/* eslint-disable camelcase */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { ModeCommentOutlined } from '@material-ui/icons';
import { PropTypes } from 'prop-types';

// Firebase
import { db, CHAT_COUNT_REF } from '../../Firebase';

// Utils
import Mixpanel from '../../../utils/mixpanel';
import { formatDateTime, getQueryParams } from '../../helperFunctions';

// Constants
import sidebarTypes from '../../components/Sidebar/constants';

// Components
import { Normal } from '../../components/Button';
import Pill from '../../components/pill';
import LabelValue from './LabelValue';

// Styles
import { Body, Footer } from '../../styles/sidebar';
import { whiteAlpha } from '../../styles/utils';
import { Flex, Text, TextButton, NoDetailsFound } from '../../styles/common';
import { colors } from '../../styles/variables';
import { AppLinkButton } from './CloudAccess/styles';

const List = styled.div`
  position: relative;
  background-color: ${({ editor }) =>
    editor ? 'transparent' : whiteAlpha(0.15)};
  margin-bottom: 1rem;
  padding: ${({ editor }) => (editor ? '0' : '1.5rem')};
  cursor: ${({ editor }) => (editor ? 'default' : 'pointer')};
  border-radius: 1rem;
  border: 1px solid transparent;
  .label {
    font-size: 1rem;
    margin-bottom: 0.8rem;
  }
  .value {
    font-size: 1.3rem;
  }

  &:hover,
  &:focus {
    border: 1px solid ${({ editor }) => (editor ? 'transparent' : colors.white)};
  }
`;

const Head = styled(Flex)`
  align-items: center;
  justify-content: space-between;
`;

const Row = styled.div`
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const Title = styled.div`
  font-size: 1.5rem;
  color: ${whiteAlpha(0.8)};
  flex: 1;
  margin-right: 3rem;
`;

const TaskDetails = props => {
  const {
    toggleModal,
    pusher,
    loadSelectedTaskDetails,
    toggleSidebar,
    tasks,
    createUpdateFetchTask,
    loadAllTaskDetails,
    selectedTask,
    toggleToast,
    updateCollaboratedUsers,
    toggleAssignedTaskFromHome,
    userDetails,
    closeSidebar,
    location,
    requestCloudSystem,
    match: { params },
  } = props;

  const taskIDsRef = [];
  const taskFromHome = useSelector(
    state => state.storyboard.assignedTaskViewFromHome
  );
  const isTaskDbIdPresentInUrl = getQueryParams(location).get('task');
  const [taskDbId] = useState(selectedTask?.task_db_id || taskFromHome || null);

  const [loading, setLoading] = useState(true);

  const [areWeFetchingMessages, toggleFetchingMessages] = useState(true);

  const noEditorGoBack = useCallback(() => {
    if (!isTaskDbIdPresentInUrl) {
      loadSelectedTaskDetails({}); // reset when URL doesnt have task id
    }
    toggleSidebar({
      title: 'Task Details',
      show: true,
      type: sidebarTypes.taskDetails,
      componentProps: {
        location,
        taskDbId,
        pusher,
        toggleModal,
        toggleAssignedTaskFromHome,
        requestCloudSystem,
      },
    });
  }, []);

  const triggerToast = (head, message, variant) =>
    toggleToast({
      message: {
        messageHead: head,
        messageBody: message,
        variant,
      },
    });

  useEffect(() => {
    (async () => {
      const request = {
        project_db_id: params.project_db_id,
        ...(taskDbId && { task_db_id: taskDbId }),
      };

      const taskDetailsResponse = await createUpdateFetchTask(request, 'get');
      const {
        data: { data, response },
      } = taskDetailsResponse;

      if (response) {
        setLoading(false);
        loadAllTaskDetails({ data: taskDbId ? [data] : data });
      }
    })();
  }, []);

  useEffect(() => {
    const allTaskIDs = tasks.map(item => item.task_db_id); // creating array of task_db_id
    allTaskIDs.forEach((item, i) => {
      /*
       * creating taskIDsRef array to track for which we are listening firebase changes.
       * if item === taskId is not the in the list then only we listen and push that task id in taskIDsRefs list
       */
      if (!taskIDsRef.includes(item)) {
        db.ref(CHAT_COUNT_REF + `/${params.project_db_id}/${item}`).on(
          'value',
          snapshot => {
            taskIDsRef.push(item);
            const values = snapshot.val() ? snapshot.val() : null;
            toggleFetchingMessages(false);
            if (values && values[userDetails.user_id]) {
              /*
               * update reducer only when firebase has logged in user id
               * so that we can update last_seen_count
               */
              const unread =
                values.chats_count -
                values[userDetails.user_id].last_seen_count;
              loadAllTaskDetails({ data: { unread, index: i }, key: 'unread' }); // to update reducer
            }
          }
        );
      }
    });

    return () => {
      // removing the listener
      allTaskIDs.forEach(item => {
        db.ref(CHAT_COUNT_REF + `/${params.project_db_id}/${item}`).off();
      });
    };
  }, [tasks.length]);

  const resendEmailInvite = (email, id) => {
    // will resend the email invite
    updateCollaboratedUsers(
      {
        project_db_id: params.project_db_id,
        task_db_id: id,
        user_email: email,
        role: 'editor',
      },
      'put'
    )
      .then(res => {
        const {
          data: { response, errormessage },
        } = res;
        if (response) {
          triggerToast('Success', 'Invite sent successfully', 'success');
        } else {
          triggerToast('Error', errormessage, 'error');
        }
      })
      .catch(err => {
        triggerToast('Error', err.data.errormessage, 'error');
      });
  };

  const renderMessages = item => {
    let message = null;
    if (areWeFetchingMessages) {
      message = 'Fetching';
    } else {
      message =
        item.unread_messages && item.unread_messages > 0
          ? `${item.unread_messages} Unread messages`
          : ' View messages';
    }
    return message;
  };

  const selectTask = async index => {
    loadSelectedTaskDetails(tasks[index]);
    toggleSidebar({
      type: sidebarTypes.createTask,
      show: true,
      title: 'Update Task',
      goBack: noEditorGoBack,
      closeCallback: () => {
        if (!isTaskDbIdPresentInUrl) {
          loadSelectedTaskDetails({});
        }

        if (taskFromHome) {
          toggleAssignedTaskFromHome();
        }
      },
      componentProps: {
        view: sidebarTypes.updateTask,
        toggleModal,
      },
    });
  };

  return (
    <>
      <Body noFooter={!taskDbId || taskFromHome}>
        {loading ? (
          <Text paddingLeft='0' paddingRight='0'>
            Fetching task details...
          </Text>
        ) : (
          <>
            {tasks && tasks.length ? (
              tasks.map((item, i) => {
                const {
                  task_type_label,
                  is_transfer_request,
                  task_status,
                  assigned_to_user_profiles,
                  invited_team_members,
                  expire_at,
                  description,
                  task_db_id,
                  assigned_by,
                } = item;

                return (
                  <List
                    key={task_db_id}
                    editor={taskDbId}
                    onClick={e =>
                      taskDbId ? e.preventDefault() : selectTask(i)
                    }
                  >
                    <Head style={{ marginBottom: '1rem' }}>
                      <Title>{task_type_label}</Title>
                      <Pill
                        status={
                          is_transfer_request ? 'Transfer Request' : task_status
                        }
                      />
                    </Head>

                    <Row>
                      <LabelValue
                        className='def-label'
                        label='Assigned By'
                        value={assigned_by && assigned_by.name}
                      />
                      {assigned_to_user_profiles.length > 0 ? (
                        <LabelValue
                          label='Assigned To'
                          value={assigned_to_user_profiles[0].name}
                        />
                      ) : (
                        <LabelValue
                          label='Assigned To'
                          value={invited_team_members[0]}
                        />
                      )}
                    </Row>

                    <Row>
                      <LabelValue
                        label='Due Date'
                        value={formatDateTime(expire_at)?.date}
                      />
                      <Flex className='items-start justify-center flex-col'>
                        <AppLinkButton
                          className='flex items-center'
                          style={{
                            fontSize: '1.3rem',
                            padding: '0 0 0.8rem 0',
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            Mixpanel.track('Task Chat');
                            toggleSidebar({
                              show: true,
                              type: sidebarTypes.chat,
                              title: 'Task Chat',
                              goBack: noEditorGoBack,
                              closeCallback: () => {
                                if (!isTaskDbIdPresentInUrl) {
                                  loadSelectedTaskDetails({}); // reset when URL doesnt have task id
                                }

                                if (taskFromHome) {
                                  toggleAssignedTaskFromHome();
                                }
                              },
                              componentProps: {
                                taskDbId: task_db_id,
                                pusher,
                                subTitle: `${task_type_label} - ${assigned_to_user_profiles[0].name}`,
                                goBackFlag: true,
                              },
                            });
                          }}
                        >
                          <span>
                            <ModeCommentOutlined
                              style={{ marginRight: '0.5rem' }}
                            />
                          </span>
                          <span style={{ marginTop: '-0.4rem' }}>
                            {renderMessages(item)}
                          </span>
                        </AppLinkButton>
                        {invited_team_members?.length > 0 && (
                          <TextButton
                            onClick={e => {
                              e.stopPropagation();
                              resendEmailInvite(
                                invited_team_members[0],
                                task_db_id
                              );
                            }}
                          >
                            Resend Invite
                          </TextButton>
                        )}
                      </Flex>
                    </Row>

                    {taskDbId && description && (
                      <Row>
                        <LabelValue label='Description' value={description} />
                      </Row>
                    )}
                  </List>
                );
              })
            ) : (
              <NoDetailsFound>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='56'
                  height='56'
                  viewBox='0 0 56 56'
                >
                  <g fill='#FFF' fillOpacity='.4'>
                    <path d='M48.316 37.825l-5.498 7.899-3.88-3.185c-.418-.344-1.038-.283-1.382.136-.344.42-.284 1.04.137 1.382l4.702 3.86c.176.146.397.223.623.223.044 0 .09-.003.134-.01.272-.037.515-.186.672-.411l6.105-8.772c.31-.446.2-1.058-.245-1.368-.446-.311-1.057-.2-1.368.246z' />
                    <path d='M53.053 35.079V11.962C53.053 5.366 47.686 0 41.09 0H11.962C5.366 0 0 5.366 0 11.962V41.09c0 6.596 5.366 11.963 11.962 11.963H35.08C37.292 54.892 40.132 56 43.229 56 50.27 56 56 50.27 56 43.228c0-3.096-1.108-5.936-2.947-8.15zm-41.09 16.009c-5.513 0-9.998-4.484-9.998-9.998V11.962c0-5.513 4.485-9.997 9.997-9.997H41.09c5.513 0 9.998 4.484 9.998 9.997v21.214c-.074-.058-.153-.108-.227-.164-.207-.155-.413-.309-.629-.451l-.07-.043c-.505-.328-1.036-.617-1.587-.872-.075-.034-.148-.074-.222-.107-.217-.095-.438-.177-.66-.26-.14-.054-.28-.108-.423-.156-.203-.068-.409-.127-.616-.185-.176-.05-.353-.096-.53-.137-.188-.043-.375-.085-.566-.12-.226-.043-.453-.076-.682-.105-.154-.02-.306-.044-.461-.059-.393-.038-.789-.06-1.187-.06-6.026 0-11.08 4.2-12.415 9.824h-4.287c-.542 0-.982.439-.982.982 0 .543.44.983.982.983h3.98c-.025.325-.05.651-.05.982 0 .398.024.794.06 1.187.015.156.038.308.059.463.03.227.062.454.104.678.036.191.078.38.122.57.041.175.086.35.135.523.059.21.118.419.188.624.046.138.1.274.15.41.085.226.169.451.265.672.028.064.063.127.092.192.262.57.561 1.12.902 1.64.009.015.017.03.026.042.143.217.298.424.453.633.056.074.106.152.163.226H11.962zm31.265 2.947c-2.771 0-5.295-1.058-7.21-2.78-.238-.213-.465-.435-.681-.667-.048-.051-.094-.104-.141-.156-.206-.228-.4-.464-.585-.708-.042-.056-.086-.112-.127-.169-.182-.251-.352-.51-.512-.775-.032-.053-.065-.105-.097-.16-.161-.278-.307-.563-.443-.854-.022-.046-.045-.09-.066-.138-.136-.303-.256-.614-.363-.929-.013-.04-.03-.077-.043-.115-.105-.32-.192-.647-.268-.977-.009-.039-.021-.076-.03-.115-.07-.326-.12-.656-.16-.988-.005-.046-.015-.091-.02-.138-.04-.376-.06-.755-.06-1.138 0-5.959 4.847-10.807 10.806-10.807.383 0 .763.02 1.14.062.045.005.087.015.131.02.334.04.667.092.995.163.037.008.072.02.11.028.332.076.66.163.982.27.036.011.072.026.108.039.318.108.632.23.938.367l.13.062c.293.137.58.285.862.447l.151.092c.268.162.53.333.784.518l.16.12c.248.187.487.384.72.592.048.045.098.088.145.133.235.22.46.45.678.692 1.718 1.913 2.773 4.434 2.773 7.202 0 5.959-4.848 10.807-10.807 10.807z' />
                    <path d='M26.526 13.754h17.685c.542 0 .982-.439.982-.982 0-.543-.44-.983-.982-.983H26.526c-.542 0-.982.44-.982.983 0 .543.44.982.982.982zM26.526 27.509h17.685c.542 0 .982-.44.982-.983 0-.543-.44-.982-.982-.982H26.526c-.542 0-.982.439-.982.982 0 .544.44.983.982.983zM18.833 7.221l-6.139 7.16-3.754-2.817c-.434-.325-1.05-.237-1.375.197-.326.433-.238 1.05.196 1.374l4.491 3.37c.177.132.383.196.59.196.278 0 .553-.117.746-.344l6.737-7.86c.353-.411.306-1.031-.106-1.384-.41-.352-1.033-.305-1.386.108zM18.833 20.975l-6.139 7.16L8.94 25.32c-.434-.325-1.05-.238-1.375.196-.326.434-.238 1.05.196 1.375l4.491 3.369c.177.132.383.196.59.196.278 0 .553-.117.746-.344l6.737-7.86c.353-.411.306-1.03-.106-1.384-.41-.351-1.033-.304-1.386.108zM18.833 35.712l-6.139 7.16-3.754-2.816c-.434-.324-1.05-.238-1.375.196-.326.433-.238 1.05.196 1.375l4.491 3.369c.177.132.383.196.59.196.278 0 .553-.117.746-.344l6.737-7.859c.353-.411.306-1.031-.106-1.384-.41-.353-1.033-.305-1.386.107z' />
                  </g>
                </svg>

                <div className='content'>
                  <div className='head'>No tasks created…</div>
                  <div className='sub-head'>
                    Tasks created by you will show up here!
                  </div>
                </div>
              </NoDetailsFound>
            )}
          </>
        )}
      </Body>

      {!loading &&
        !taskFromHome &&
        taskDbId &&
        !selectedTask.is_transfer_request && (
          <Footer>
            <Normal
              style={{ marginRight: '2rem', padding: '1.5rem' }}
              color='secondary'
              onClick={closeSidebar}
              fontSize='1.2rem'
            >
              Close
            </Normal>
            <Normal
              onClick={() => requestCloudSystem()}
              fontSize='1.2rem'
              style={{ padding: '1.5rem' }}
            >
              Request System
            </Normal>
          </Footer>
        )}
      {!tasks && !taskDbId && (
        <Footer>
          <Normal
            onClick={() => {
              toggleSidebar({
                type: sidebarTypes.createTask,
                show: true,
                title: 'Create Task',
                goBack: noEditorGoBack,
                componentProps: {
                  loadSelectedTaskDetails,
                },
              });
            }}
          >
            Create
          </Normal>
        </Footer>
      )}
    </>
  );
};

TaskDetails.propTypes = {
  createUpdateFetchTask: PropTypes.func,
  loadSelectedTaskDetails: PropTypes.func,
  tasks: PropTypes.instanceOf(Object),
  loadAllTaskDetails: PropTypes.instanceOf(Object),
  selectedTask: PropTypes.instanceOf(Object),
  updateCollaboratedUsers: PropTypes.func,
  toggleAssignedTaskFromHome: PropTypes.func,
  userDetails: PropTypes.instanceOf(Object),
  location: PropTypes.instanceOf(Object),
  match: PropTypes.instanceOf(Object),
  toggleToast: PropTypes.func,
  closeSidebar: PropTypes.func,
  toggleModal: PropTypes.func,
  pusher: PropTypes.instanceOf(Object),
  toggleSidebar: PropTypes.func,
  requestCloudSystem: PropTypes.func,
};

export default TaskDetails;
